import { defineStore } from 'pinia'
import { authClient, apiFor } from '../services/api'
import { useModeStore } from './mode'

interface PortalUser {
  id: string
  firstname: string
  lastname: string
  phone: string
  memberRole: 'OWNER' | 'DEVELOPER'
}

interface Partner {
  id: string
  name: string
  status: 'ACTIVE' | 'SUSPENDED'
  billingMode: string
  requestedBillingMode?: string | null
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as PortalUser | null,
    partner: null as Partner | null,
    isAuthenticated: false,
    isLoading: false,
  }),

  getters: {
    isOwner: (state) => state.user?.memberRole === 'OWNER',
    isSuspended: (state) => state.partner?.status === 'SUSPENDED',
    fullName: (state) =>
      state.user ? `${state.user.firstname} ${state.user.lastname}`.trim() : '',
  },

  actions: {
    async checkAuth(): Promise<boolean> {
      this.isLoading = true
      try {
        const { data } = await authClient.get('/partner-portal/auth/me')
        this.user = data.user
        this.partner = data.partner
        this.isAuthenticated = true
        return true
      } catch {
        this.clear()
        return false
      } finally {
        this.isLoading = false
      }
    },

    setSession(user: PortalUser, partner: Partner) {
      this.user = user
      this.partner = partner
      this.isAuthenticated = true
    },

    async logout() {
      const modeStore = useModeStore()
      try {
        // Fermer la session sur les deux environnements : le cookie est posé
        // séparément par chacun, en déconnecter un seul laisserait l'autre ouvert.
        await Promise.allSettled([
          apiFor('LIVE').post('/partner-portal/auth/logout'),
          apiFor('TEST').post('/partner-portal/auth/logout'),
        ])
      } finally {
        this.clear()
        modeStore.setMode('TEST')
      }
    },

    clear() {
      this.user = null
      this.partner = null
      this.isAuthenticated = false
    },
  },
})
